import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, CheckCircle, XCircle, X } from "lucide-react";
import axios from "axios";
import Apiurl from "../../Environment/Apiurl";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assests/css/Toast.css";
import logo from "../../assests/image/Logo1.png";
import bg from "../../assests/image/Login 2.jpg";
import { usePermissions } from "../../context/PermissionContext"; // ✅ ADD THIS LINE
import { isAuthenticated } from "../../utils/auth";

// Toast Component with Portal
const Toast = ({ message, type, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const toastContent = (
    <div className={`custom-toast ${type}`}>
      <div className="toast-content">
        {type === "success" ? (
          <CheckCircle size={24} className="toast-icon" />
        ) : (
          <XCircle size={24} className="toast-icon" />
        )}
        <span>{message}</span>
        <button className="toast-close" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(toastContent, document.body);
};

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const navigate = useNavigate();
  const { reloadPermissions } = usePermissions(); // ✅ NOW WORKS WITH IMPORT ABOVE

  React.useEffect(() => {
    if (isAuthenticated()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ show: false, message: "", type: "" });
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      showToast("Please enter both User ID and password", "error");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${Apiurl}/login`, {
        userID: username.trim(),
        password: password,
      });

      console.log("Login response:", response.data);

      if (response.data.success) {
        // ✅ STEP 1: Store everything FIRST
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userID", response.data.user.userID);
        localStorage.setItem("userId", response.data.user._id);
        localStorage.setItem("fullName", response.data.user.fullName);
        localStorage.setItem("role", response.data.user.role);
        // localStorage.setItem("expiry", Date.now() + 60 * 60 * 1000); // 1 hour
        localStorage.setItem("expiry", Date.now() + 8 * 60 * 60 * 1000); // 8 hours

        // ✅ STEP 2: Load permissions BEFORE navigating
        await reloadPermissions();

        // ✅ STEP 3: Show success message
        showToast("Login successful! Redirecting...", "success");

        // ✅ STEP 4: Redirect LAST (after 1.5 seconds)
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1200);
      }
    } catch (error) {
      console.error("Login error:", error);
      showToast(
        error.response?.data?.message ||
          "Invalid credentials. Please try again.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}

      <div
        style={{
          height: "100vh",
          width: "100%",
          background: `url('${bg}') no-repeat center center/cover`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingRight: "50px",
        }}
      >
        <div
          className="card p-5 shadow-lg"
          style={{
            width: "400px",
            borderRadius: "1rem",
            backgroundColor: "#fff",
            backdropFilter: "blur(10px)",
          }}
        >
          <div className="text-center mb-4">
            <img src={logo} alt="Logo" style={{ maxWidth: "120px" }} />
            <h4 className="mt-3 mb-1">Welcome Back</h4>
            <p className="text-muted small">
              Enter your credentials to continue
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label fw-semibold">
                User ID
              </label>
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                placeholder="Enter your User ID"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                required
                autoComplete="username"
              />
            </div>

            <div className="mb-3 position-relative">
              <label htmlFor="password" className="form-label fw-semibold">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                id="password"
                name="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                autoComplete="current-password"
                style={{ paddingRight: "40px" }}
              />
              <span
                className="position-absolute"
                style={{
                  cursor: "pointer",
                  right: "10px",
                  top: "38px",
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>

            <div className="d-grid mt-4">
              <button
                type="submit"
                className="btn btn-gradient btn-lg"
                disabled={loading}
                style={{
                  background: "linear-gradient(to right, #00c6ff, #0072ff)",
                  color: "#fff",
                  border: "none",
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </div>
          </form>

          <div className="text-center mt-3">
            <small className="text-muted">
              Need help? Contact your administrator
            </small>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
